import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronDown,
  FiDownload,
  FiEdit2,
  FiEye,
  FiFileText,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import LoaderComp from "../../../components/LoaderComp";
import PaginationComp from "../../../components/PaginationComp";
import RenderToast from "../../../components/RenderToast";
import useDebounce from "../../../hooks/useDebounce";
import ConfirmModal from "../../../Modal/ConfirmModal";
import EditProductModal from "../../../Modal/Product/EditProductModal";
import ViewProductModal from "../../../Modal/Product/ViewProductModal";
import API from "../../../utils/api";
import classes from "./ViewProducts.module.css";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const formatPakistanDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    const pakistanDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Karachi" }),
    );
    const day = String(pakistanDate.getDate()).padStart(2, "0");
    const month = String(pakistanDate.getMonth() + 1).padStart(2, "0");
    const year = pakistanDate.getFullYear();
    let hours = pakistanDate.getHours();
    const minutes = String(pakistanDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, "0");
    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch {
    return "N/A";
  }
};

const SCREEN_OPTION_FIELDS = [
  { key: "id", label: "ID" },
  { key: "sku", label: "SKU" },
  { key: "productName", label: "Product Name" },
  { key: "productShortDesc", label: "Short Description" },
  { key: "productDesc", label: "Description" },
  { key: "productImage", label: "Product Image" },
  { key: "productGallery", label: "Product Gallery" },
  { key: "brand_id", label: "Brand ID" },
  { key: "brandName", label: "Brand Name" },
  { key: "category_id", label: "Category ID" },
  { key: "categoryPath", label: "Category Path" },
  { key: "inStock", label: "In Stock" },
  { key: "condition", label: "Condition" },
  { key: "availability", label: "Availability" },
  { key: "regularPrice", label: "Regular Price" },
  { key: "salePrice", label: "Sale Price" },
  { key: "status", label: "Status" },
  { key: "metaTitle", label: "Meta Title" },
  { key: "metaDescription", label: "Meta Description" },
  { key: "product_slug", label: "Product Slug" },
  { key: "created_via", label: "Created Via" },
  { key: "created_by", label: "Created By (User ID)" },
  { key: "author_name", label: "Author Name" },
  { key: "created_at", label: "Created At" },
  // Schema is auto-generated, removed from view
];

const SCREEN_OPTION_STORAGE_KEY = "productViewScreenOptions";
const SCREEN_OPTION_KEYS = SCREEN_OPTION_FIELDS.map((field) => field.key);

const parseGalleryValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return typeof value === "string"
      ? value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
  }
};

export default function ViewProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  // search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  // filters
  const [brandFilter, setBrandFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");

  // modals
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  // multi-select
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [actionMode, setActionMode] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState("");

  // supporting lists
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const categoryIndex = useMemo(() => {
    const map = new Map();
    categories.forEach((cat) => {
      if (cat?.id) {
        map.set(Number(cat.id), cat);
      }
    });
    return map;
  }, [categories]);

  // const getCategoryPath = useCallback(
  //   (categoryId) => {
  //     const id = Number(categoryId);
  //     if (!id || !categoryIndex.has(id)) return "";
  //     const names = [];
  //     const visited = new Set();
  //     let current = categoryIndex.get(id);
  //     while (current && !visited.has(current.id)) {
  //       if (current.categoryName) names.unshift(current.categoryName);
  //       visited.add(current.id);
  //       const parentId = Number(current.parent_id);
  //       current = parentId ? categoryIndex.get(parentId) : null;
  //     }
  //     return names.join(" > ");
  //   },
  //   [categoryIndex],
  // );

  const getCategoryPath = useCallback(
    (categoryId) => {
      const id = Number(categoryId);
      if (!id || !categoryIndex.has(id)) return "";
      const category = categoryIndex.get(id);
      return category?.categoryName || "";
    },
    [categoryIndex],
  );

  // confirm modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [user, setUser] = useState(readUser());
  const [statusFilter, setStatusFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const getInitialColumns = () => {
    if (typeof window === "undefined") {
      return new Set(SCREEN_OPTION_KEYS);
    }
    try {
      const stored = window.localStorage.getItem(SCREEN_OPTION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const valid = parsed.filter((key) =>
            SCREEN_OPTION_KEYS.includes(key),
          );
          if (valid.length) {
            return new Set(valid);
          }
        }
      }
    } catch {
      // ignore malformed storage
    }
    return new Set(SCREEN_OPTION_KEYS);
  };
  const [visibleColumns, setVisibleColumns] = useState(getInitialColumns);
  const [screenOptionsOpen, setScreenOptionsOpen] = useState(false);
  const screenOptionsRef = useRef(null);

  useEffect(() => {
    const handleStorage = () => setUser(readUser());
    const handleUserUpdate = () => setUser(readUser());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("userUpdated", handleUserUpdate);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      SCREEN_OPTION_STORAGE_KEY,
      JSON.stringify([...visibleColumns]),
    );
  }, [visibleColumns]);

  useEffect(() => {
    if (!screenOptionsOpen) return;
    const handleClickOutside = (event) => {
      if (
        screenOptionsRef.current &&
        !screenOptionsRef.current.contains(event.target)
      ) {
        setScreenOptionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [screenOptionsOpen]);

  const can = (permission) => user && Number(user[permission]) === 1;
  const canViewProducts = can("product_view");
  const canAddProductButton = can("product_view_add_button");
  const canImportProductButton = can("product_view_import_button");
  const canExportProducts = can("product_view_export_button");
  const canEditProduct = can("product_view_edit_action");
  const canViewProductAction = can("product_view_view_action");
  const canDeleteProduct = can("product_view_delete_action");

  useEffect(() => {
    fetchBrandsAndCategories();
  }, []);

  // useEffect(() => {
  //   fetchProducts(statusFilter);
  // }, [statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(
      statusFilter,
      categoryFilter,
      brandFilter,
      conditionFilter,
      debouncedSearch,
    );
  }, [
    statusFilter,
    categoryFilter,
    brandFilter,
    conditionFilter,
    debouncedSearch,
  ]);

  useEffect(() => {
    fetchProducts(
      statusFilter,
      categoryFilter,
      brandFilter,
      conditionFilter,
      debouncedSearch,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchProducts = async (
    statusValue = "",
    categoryId = "",
    brandId = "",
    condition = "",
    searchTerm = "",
  ) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({ action: "fetch" });
      if (statusValue) {
        params.append("status", statusValue);
      }
      if (categoryId) params.append("category_id", categoryId);
      if (brandId) params.append("brand_id", brandId);
      if (condition) params.append("condition", condition);
      if (searchTerm && searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      // Add pagination
      params.append("page", currentPage.toString());
      params.append("per_page", perPage.toString());

      // Include schema data for SEO tools
      params.append("include_schema", "true");

      const response = await fetch(`${API.base}/products.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await response.json();

      // Handle paginated response
      if (data.pagination) {
        setProducts(data.data || []);
        setTotalProducts(data.pagination.total || 0);
      } else if (Array.isArray(data)) {
        setProducts(data);
        setTotalProducts(data.length);
      } else if (data.data) {
        setProducts(data.data);
        setTotalProducts(data.data.length);
      } else {
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (err) {
      console.error(err);
      RenderToast({ type: "error", message: "Failed to load products" });
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandsAndCategories = async () => {
    try {
      const brandsRes = await API.post("/brands.php", { action: "fetch" });
      const categoriesRes = await API.post("/categories.php", {
        action: "fetch",
      });
      if (brandsRes.status === 200) {
        const brandsData = Array.isArray(brandsRes.data)
          ? brandsRes.data
          : brandsRes.data.data || [];
        setBrands(brandsData);
      }
      if (categoriesRes.status === 200) {
        const categoriesData = Array.isArray(categoriesRes.data)
          ? categoriesRes.data
          : categoriesRes.data.data || [];
        setCategories(categoriesData);
      }
    } catch (err) {
      console.error("Failed to load brands/categories:", err);
    }
  };

  const handleToggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) {
          RenderToast({
            type: "info",
            message: "At least one column must remain visible",
          });
          return prev;
        }
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(new Set(SCREEN_OPTION_KEYS));
  };

  const apiBaseUrl = API.base?.endsWith("/primecloudsystems_api")
    ? API.base
    : `${API.base}/primecloudsystems_api`;

  const getImageUrl = useCallback(
    (imageName) => {
      if (!imageName) {
        return `${apiBaseUrl}/upload/placeholder-image.jpg`;
      }
      return `${apiBaseUrl}/upload/${imageName}`;
    },
    [apiBaseUrl],
  );

  // Use placeholder
  const PRODUCT_PLACEHOLDER = `${apiBaseUrl}/upload/placeholder-image.jpg`;

  const handleOpenEdit = (product) => {
    if (!canEditProduct) return;
    setActiveProduct(product);
    setShowEdit(true);
  };
  const handleOpenView = (product) => {
    if (!canViewProductAction) return;
    setActiveProduct(product);
    setShowView(true);
  };

  const handleAfterUpdate = () => {
    setShowEdit(false);
    setActiveProduct(null);
    fetchProducts(statusFilter);
  };

  const truncateText = useCallback((text, max = 60) => {
    if (!text && text !== 0) return "N/A";
    const str = String(text);
    return str.length <= max ? str : `${str.substring(0, max)}…`;
  }, []);

  const formatPrice = useCallback((value) => {
    if (value === null || value === undefined || value === "") return "—";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return `$${numeric.toFixed(2)}`;
  }, []);

  // Since search is now server-side, we don't need client-side filtering
  // But keep this for backward compatibility if needed
  const filteredProducts = useMemo(() => {
    // If we have server-side pagination, products are already filtered
    if (totalProducts > 0) return products;
    // Otherwise, apply client-side filtering
    if (!debouncedSearch?.trim()) return products;
    return products.filter((p) =>
      String(p.sku || "")
        .toLowerCase()
        .includes(debouncedSearch.toLowerCase()),
    );
  }, [products, debouncedSearch, totalProducts]);

  // Use server-side total if available, otherwise fall back to client-side filtering
  const total = totalProducts > 0 ? totalProducts : filteredProducts.length;
  const totalPages = Math.ceil(total / perPage);

  // For server-side pagination, use products directly (already paginated and filtered)
  // For client-side, use filteredProducts
  const paginated =
    totalProducts > 0
      ? products
      : filteredProducts.slice(
          (currentPage - 1) * perPage,
          currentPage * perPage,
        );

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    setSelectedProducts(new Set());
    setActionMode(false);
  }, [statusFilter]);
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // multi-select
  const toggleSelectProduct = (id) => {
    if (!canDeleteProduct) return;
    const newSet = new Set(selectedProducts);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedProducts(newSet);
    setActionMode(newSet.size > 0);
  };
  const selectAllCurrentPage = () => {
    if (!canDeleteProduct) return;
    const newSet = new Set(selectedProducts);
    paginated.forEach((p) => newSet.add(p.id));
    setSelectedProducts(newSet);
    setActionMode(newSet.size > 0);
  };
  const clearSelection = () => {
    setSelectedProducts(new Set());
    setActionMode(false);
  };

  // delete selected products
  const handleDeleteSelected = () => {
    if (!canDeleteProduct) return;
    if (!selectedProducts.size) return;
    setPendingDelete("selected");
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem("authToken");
    let deletedCount = 0;

    if (pendingDelete === "all") {
      // Delete all products using the new delete_all action
      setDeletingAll(true);
    }

    if (pendingDelete === "selected") {
      for (let id of selectedProducts) {
        try {
          const form = new FormData();
          form.append("action", "delete");
          form.append("id", id);
          const res = await fetch(`${API.base}/products.php`, {
            method: "POST",
            headers: { Authorization: token },
            body: form,
          });
          const data = await res.json();
          if (data.success) deletedCount++;
        } catch (err) {
          console.error(err);
        }
      }
      clearSelection();
    } else if (pendingDelete === "all") {
      // Delete all products using the new delete_all action
      setDeletingAll(true);
      try {
        const params = new URLSearchParams({ action: "delete_all" });
        const res = await fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: params,
        });
        const data = await res.json();
        if (data.success) {
          deletedCount = totalProducts; // Assume all were deleted
        } else {
          throw new Error(data.error || "Failed to delete all products");
        }
      } catch (err) {
        console.error("Failed to delete all products:", err);
        RenderToast({
          type: "error",
          message: err.message || "Failed to delete all products",
        });
        setShowConfirm(false);
        setPendingDelete(null);
        setDeletingAll(false);
        return;
      } finally {
        setDeletingAll(false);
      }
    } else if (pendingDelete?.id) {
      try {
        const form = new FormData();
        form.append("action", "delete");
        form.append("id", pendingDelete.id);
        const res = await fetch(`${API.base}/products.php`, {
          method: "POST",
          headers: { Authorization: token },
          body: form,
        });
        const data = await res.json();
        if (data.success) deletedCount++;
      } catch (err) {
        console.error(err);
      }
    }

    RenderToast({
      type: deletedCount ? "success" : "error",
      message: `${deletedCount} product(s) deleted.`,
    });
    fetchProducts(statusFilter, categoryFilter, brandFilter, conditionFilter);
    setShowConfirm(false);
    setPendingDelete(null);
    setCurrentPage(1);
  };

  const columnRenderers = {
    id: {
      header: "ID",
      cell: (product) => product.id ?? "—",
    },
    sku: {
      header: "SKU",
      cell: (product) => (
        <span className={classes.mono}>{product.sku || "N/A"}</span>
      ),
    },
    productName: {
      header: "Product Name",
      cell: (product) => truncateText(product.productName, 40),
    },
    productShortDesc: {
      header: "Short Description",
      cell: (product) => truncateText(product.productShortDesc, 40),
    },
    productDesc: {
      header: "Description",
      cell: (product) => truncateText(product.productDesc, 60),
    },
    productImage: {
      header: "Product Image",
      cell: (product) => (
        <div className={classes.thumb}>
          <img
            src={getImageUrl(product.productImage)}
            alt={product.productName}
            onError={(e) => (e.currentTarget.src = PRODUCT_PLACEHOLDER)}
          />
        </div>
      ),
    },
    productGallery: {
      header: "Product Gallery",
      cell: (product) => {
        const galleryItems = parseGalleryValue(product.productGallery);
        if (!galleryItems.length) return "—";
        return (
          <div className={classes.galleryPreview}>
            {galleryItems.slice(0, 3).map((image, idx) => (
              <img
                key={`${image}-${idx}`}
                src={getImageUrl(image)}
                alt="Gallery"
                onError={(e) => (e.currentTarget.src = PRODUCT_PLACEHOLDER)}
              />
            ))}
            {galleryItems.length > 3 && (
              <span className={classes.galleryMore}>
                +{galleryItems.length - 3}
              </span>
            )}
          </div>
        );
      },
    },
    brand_id: {
      header: "Brand ID",
      cell: (product) => product.brand_id ?? "—",
    },
    brandName: {
      header: "Brand Name",
      cell: (product) => product.brandName || "N/A",
    },
    category_id: {
      header: "Category ID",
      cell: (product) => product.category_id ?? "—",
    },
    categoryPath: {
      header: "Category",
      cell: (product) => getCategoryPath(product.category_id) || "—",
    },
    inStock: {
      header: "In Stock",
      cell: (product) => (product.inStock ?? "0").toString(),
    },
    condition: {
      header: "Condition",
      cell: (product) => {
        const conditionValue = (product.condition || "N/A").toLowerCase();
        const badgeClass =
          conditionValue === "new" ? classes.badgeGreen : classes.badgeYellow;
        return (
          <span className={`${classes.badge} ${badgeClass}`}>
            {product.condition || "N/A"}
          </span>
        );
      },
    },
    availability: {
      header: "Availability",
      cell: (product) => product.availability || "N/A",
    },
    regularPrice: {
      header: "Regular Price",
      cell: (product) => formatPrice(product.regularPrice),
    },
    salePrice: {
      header: "Sale Price",
      cell: (product) => formatPrice(product.salePrice),
    },
    status: {
      header: "Status",
      cell: (product) => {
        const statusValue = (product.status || "draft").toLowerCase();
        const statusClass =
          statusValue === "publish"
            ? classes.statusPublish
            : classes.statusDraft;
        return (
          <span className={`${classes.statusBadge} ${statusClass}`}>
            {statusValue}
          </span>
        );
      },
    },
    metaTitle: {
      header: "Meta Title",
      cell: (product) => truncateText(product.metaTitle, 40),
    },
    metaDescription: {
      header: "Meta Description",
      cell: (product) => truncateText(product.metaDescription, 60),
    },
    product_slug: {
      header: "Product Slug",
      cell: (product) => {
        const slug = product.product_slug || product.productSlug || product.sku;
        return <span className={classes.mono}>{slug || "—"}</span>;
      },
    },
    created_via: {
      header: "Created Via",
      cell: (product) =>
        (product.created_via || "manual").toString().toUpperCase(),
    },
    created_by: {
      header: "Created By (User ID)",
      cell: (product) => product.created_by ?? "—",
    },
    author_name: {
      header: "Author Name",
      cell: (product) => product.author_name || "N/A",
    },
    created_at: {
      header: "Created At",
      cell: (product) => formatPakistanDateTime(product.created_at),
    },
    // Schema is auto-generated, removed from view
  };
  const totalTableColumns =
    visibleColumns.size + (canDeleteProduct ? 1 : 0) + 2;

  const handleExportProducts = async () => {
    if (!canExportProducts) return;
    const token = localStorage.getItem("authToken");
    if (!token) {
      return RenderToast({
        type: "error",
        message: "Authentication required",
      });
    }
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append("action", "export_csv");
      if (statusFilter) params.append("status", statusFilter);
      if (debouncedSearch.trim())
        params.append("search", debouncedSearch.trim());

      const response = await fetch(`${API.base}/products.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || contentType.includes("application/json")) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Failed to export products");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products_${new Date()
        .toISOString()
        .replace(/[-:]/g, "")
        .slice(0, 15)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      RenderToast({ type: "success", message: "Products exported" });
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setExporting(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setPendingDelete(null);
    setDeletingAll(false);
  };

  const handleDeleteSingle = (product) => {
    if (!canDeleteProduct) return;
    setPendingDelete(product);
    setShowConfirm(true);
  };

  if (!canViewProducts) {
    return (
      <DashboardLayout>
        <div className={classes.page}>
          <div className={classes.emptyRow}>
            You do not have permission to view products.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={classes.page}>
        {/* Top controls */}
        <div className={classes.rowTop}>
          <h3 className={classes.pageTitle}>View Products</h3>
          <div className={classes.controlsWrap}>
            {canAddProductButton && (
              <button
                className={classes.btnPrimary}
                onClick={() => navigate("/product/add")}
              >
                <FiPlus /> Add Product
              </button>
            )}
            {canImportProductButton && (
              <button
                className={classes.btnSecondary}
                onClick={() => navigate("/product/import")}
              >
                <FiDownload /> Import Products
              </button>
            )}
            {canExportProducts && (
              <button
                className={classes.btnSecondary}
                onClick={handleExportProducts}
                disabled={exporting}
              >
                <FiFileText />
                {exporting ? "Exporting..." : "Export CSV"}
              </button>
            )}

            {canDeleteProduct && (
              <button
                className={classes.btnSecondary}
                onClick={() => {
                  setPendingDelete("all");
                  setShowConfirm(true);
                }}
                disabled={deletingAll}
                style={{ backgroundColor: "#dc3545", color: "white" }}
              >
                <FiTrash2 /> Delete All Products
              </button>
            )}

            {actionMode && canDeleteProduct && (
              <button
                className={classes.btnDelete}
                onClick={handleDeleteSelected}
              >
                Delete Selected ({selectedProducts.size})
              </button>
            )}
            <div className={classes.screenOptionsWrap} ref={screenOptionsRef}>
              <button
                type="button"
                className={classes.screenOptionsToggle}
                onClick={() => setScreenOptionsOpen((prev) => !prev)}
              >
                <span>Screen Options</span>
                <FiChevronDown
                  className={`${classes.screenOptionsIcon} ${
                    screenOptionsOpen ? classes.screenOptionsIconOpen : ""
                  }`}
                />
              </button>
              {screenOptionsOpen && (
                <div className={classes.screenOptionsPanel}>
                  <div className={classes.screenOptionsTitle}>
                    Show on screen
                  </div>
                  <div className={classes.screenOptionsList}>
                    {SCREEN_OPTION_FIELDS.map(({ key, label }) => (
                      <label key={key} className={classes.screenOptionItem}>
                        <input
                          type="checkbox"
                          checked={visibleColumns.has(key)}
                          onChange={() => handleToggleColumn(key)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  <div className={classes.screenOptionsFooter}>
                    <button
                      type="button"
                      className={classes.screenOptionsReset}
                      onClick={handleResetColumns}
                    >
                      Reset to defaults
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={classes.filtersRow}>
          <div className={classes.searchWrap}>
            <FiSearch className={classes.searchIcon} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SKU or Product Name..."
              className={classes.searchInput}
              type="search"
            />
          </div>
          <div className={classes.filterInline}>
            <label htmlFor="statusFilter">Status</label>
            <select
              id="statusFilter"
              className={classes.statusSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="publish">Publish</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          <div className={classes.filterInline}>
            <label htmlFor="categoryFilter">Category</label>
            <select
              id="categoryFilter"
              className={classes.statusSelect}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.categoryName}
                </option>
              ))}
            </select>
          </div>

          <div className={classes.filterInline}>
            <label htmlFor="brandFilter">Brand</label>
            <select
              id="brandFilter"
              className={classes.statusSelect}
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <option value="">All Brands</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.brandName}
                </option>
              ))}
            </select>
          </div>

          <div className={classes.filterInline}>
            <label htmlFor="conditionFilter">Condition</label>
            <select
              id="conditionFilter"
              className={classes.statusSelect}
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
            >
              <option value="">All Conditions</option>
              <option value="New">New</option>
              <option value="Used">Used</option>
              <option value="Refurbished">Refurbished</option>
              <option value="Open Box">Open Box</option>
            </select>
          </div>
          <div className={classes.filterInline}>
            <button
              className={classes.btnClearFilters}
              onClick={() => {
                setStatusFilter("");
                setCategoryFilter("");
                setBrandFilter("");
                setConditionFilter("");
                setSearch("");
                setCurrentPage(1);
              }}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table */}
        <div className={classes.rowBottom}>
          <div className={classes.tableCard}>
            {loading ? (
              <LoaderComp />
            ) : (
              <>
                <div className={classes.tableWrapper}>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        {canDeleteProduct && (
                          <th>
                            <input
                              type="checkbox"
                              onChange={(e) =>
                                e.target.checked
                                  ? selectAllCurrentPage()
                                  : clearSelection()
                              }
                              checked={
                                paginated.every((p) =>
                                  selectedProducts.has(p.id),
                                ) && paginated.length > 0
                              }
                            />
                          </th>
                        )}
                        <th>S.No</th>
                        {SCREEN_OPTION_FIELDS.map(({ key, label }) =>
                          visibleColumns.has(key) ? (
                            <th key={key}>
                              {columnRenderers[key]?.header || label}
                            </th>
                          ) : null,
                        )}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td
                            colSpan={totalTableColumns}
                            className={classes.emptyRow}
                          >
                            No products found
                          </td>
                        </tr>
                      ) : (
                        paginated.map((p, idx) => (
                          <tr key={p.id}>
                            {canDeleteProduct && (
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedProducts.has(p.id)}
                                  onChange={() => toggleSelectProduct(p.id)}
                                />
                              </td>
                            )}
                            <td>{(currentPage - 1) * perPage + idx + 1}</td>
                            {SCREEN_OPTION_FIELDS.map(({ key }) =>
                              visibleColumns.has(key) ? (
                                <td key={key}>
                                  {columnRenderers[key]?.cell
                                    ? columnRenderers[key].cell(p, idx)
                                    : (p[key] ?? "—")}
                                </td>
                              ) : null,
                            )}
                            <td>
                              <div className={classes.actionGroup}>
                                {canViewProductAction && (
                                  <button
                                    className={classes.iconBtn}
                                    onClick={() => handleOpenView(p)}
                                    title="View"
                                  >
                                    <FiEye />
                                  </button>
                                )}
                                {canEditProduct && (
                                  <button
                                    className={classes.iconBtnGreen}
                                    onClick={() => handleOpenEdit(p)}
                                    title="Edit"
                                  >
                                    <FiEdit2 />
                                  </button>
                                )}
                                {canDeleteProduct && (
                                  <button
                                    className={classes.iconBtnRed}
                                    onClick={() => handleDeleteSingle(p)}
                                    title="Delete"
                                  >
                                    <FiTrash2 />
                                  </button>
                                )}
                                {!canViewProductAction &&
                                  !canEditProduct &&
                                  !canDeleteProduct && (
                                    <span className={classes.noActions}>—</span>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className={classes.paginationWrap}>
                  {/* Total products count */}
                  <div className={classes.totalCount}>
                    Total Products: <strong>{total}</strong>
                  </div>
                  <PaginationComp
                    total={total}
                    perPage={perPage}
                    currentPage={currentPage}
                    onPageChange={(p) => setCurrentPage(p)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modals */}
        {showEdit && activeProduct && (
          <EditProductModal
            product={activeProduct}
            onClose={() => setShowEdit(false)}
            onUpdated={handleAfterUpdate}
            getImageUrl={getImageUrl}
            brands={brands}
            categories={categories}
          />
        )}
        {showView && activeProduct && (
          <ViewProductModal
            product={activeProduct}
            onClose={() => setShowView(false)}
            getImageUrl={getImageUrl}
          />
        )}

        {/* Confirm Delete Modal */}
        <ConfirmModal
          show={showConfirm}
          title="Confirm Delete"
          message={`Are you sure you want to delete ${
            pendingDelete === "all"
              ? "ALL products"
              : pendingDelete === "selected"
                ? selectedProducts.size
                : pendingDelete?.sku
          } product(s)? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          loading={deletingAll}
        />
      </div>
    </DashboardLayout>
  );
}
